import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import i18n, { savedLanguage } from '../index';

// Hardcoded French text → English, applied to the rendered DOM (EN mode only).
const ATTRS = ['placeholder', 'aria-label', 'title'];
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE']);
const PATTERNS = [
  [/^(\d[\d\s]*) biens? correspond(?:ent)? à vos critères$/, '$1 properties match your criteria'],
  [/^(\d+) biens?$/, (m, n) => `${n} ${n === '1' ? 'property' : 'properties'}`],
  [/^(\d+) chambres?$/, (m, n) => `${n} bedroom${n === '1' ? '' : 's'}`],
  [/^(\d+) jours?$/, (m, n) => `${n} day${n === '1' ? '' : 's'}`],
  [/^il y a (\d+) jours?$/, (m, n) => `${n} day${n === '1' ? '' : 's'} ago`],
  [/^(\d+) propriétés? trouvées?$/, (m, n) => `${n} ${n === '1' ? 'property' : 'properties'} found`],
  [/^(\d+) biens? trouvés?$/, (m, n) => `${n} ${n === '1' ? 'property' : 'properties'} found`],
  [/^\/mois$/, '/month'],
  [/^© (\d{4}) Realty237\. Tous droits réservés\.$/, '© $1 Realty237. All rights reserved.'],
  [/^Aller à la slide (\d+)$/, 'Go to slide $1'],
  [/^(\d+) salles? de bains?$/, (m, n) => `${n} bathroom${n === '1' ? '' : 's'}`],
  [/^Renvoyer \((\d+)s\)$/, 'Resend ($1s)'],
  [/^(\d+)j$/, '$1d'],
  [/^Voir les (\d+) photos$/, 'View the $1 photos'],
  [/^(\d+) sur (\d+) annonces actives$/, '$1 of $2 active listings'],
  [/^(\d+) annonces?$/, (m, n) => `${n} listing${n === '1' ? '' : 's'}`],
  [/^(\d+) annonces? actives?$/, (m, n) => `${n} active listing${n === '1' ? '' : 's'}`],
  [/^Téléphone \((\d+)\)$/, 'Phone ($1)'],
  [/^(\d+) crédits$/, '$1 credits'],
  [/^(\d+) crédits \/ mois$/, '$1 credits / month'],
  [/^(\d+) crédits \+ (\d+) bonus$/, '$1 credits + $2 bonus'],
  [/^(\d+) clés max$/, '$1 keys max'],
  [/^(\d+) biens immobiliers au Cameroun \| Realty237$/, '$1 properties in Cameroon | Realty237'],
  [/^Bonjour (.+) — prêt\(e\) à publier votre première annonce \?$/, 'Hello $1 — ready to publish your first listing?'],
  [/^Bonjour (.+)$/, 'Hello $1'],
  [/^(.+) — ouvrir le détail$/, (m, x) => `${lookup(x) || x} — open details`],
  [/^Carte (.+)$/, 'Map $1'],
  [/^\/jour$/, '/day'],
  [/^\/nuit$/, '/night'],
  [/^\/an$/, '/year'],
];

let dict = null;
const norm = (s) => s.replace(/[’‘]/g, "'").replace(/\s+/g, ' ').trim();

const lookup = (raw) => {
  const key = norm(raw);
  if (!key) return null;
  if (dict[key]) return dict[key];
  for (const [re, rep] of PATTERNS) if (re.test(key)) return key.replace(re, rep);
  return null;
};

const applied = new WeakMap();

const translateText = (node) => {
  const v = node.nodeValue;
  if (!v || applied.get(node) === v) return;
  const t = lookup(v);
  if (!t) return;
  const lead = v.match(/^\s*/)[0];
  const trail = v.match(/\s*$/)[0];
  const next = lead + t + trail;
  applied.set(node, next);
  node.nodeValue = next;
};

const translateEl = (el) => {
  ATTRS.forEach((a) => {
    const v = el.getAttribute && el.getAttribute(a);
    if (v) {
      const t = lookup(v);
      if (t) el.setAttribute(a, t);
    }
  });
};

const skip = (el) => !el || SKIP_TAGS.has(el.tagName) || el.closest?.('[data-no-translate]');

const walk = (root) => {
  if (root.nodeType === Node.TEXT_NODE) {
    if (!skip(root.parentElement)) translateText(root);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE || skip(root)) return;
  translateEl(root);
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let n = tw.nextNode();
  while (n) {
    if (n.nodeType === Node.TEXT_NODE) {
      if (!skip(n.parentElement)) translateText(n);
    } else if (!skip(n)) translateEl(n);
    n = tw.nextNode();
  }
};

export const DomTranslator = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  const active = i18n.language === 'en' && !isAdmin;

  // Admin stays French: crossing the admin boundary with EN saved reloads the page in the right language.
  useEffect(() => {
    if (savedLanguage() === 'en' && (isAdmin ? i18n.language === 'en' : i18n.language !== 'en')) {
      window.location.reload();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!active) return undefined;
    let observer;
    let queue = new Set();
    let raf = 0;
    const flush = () => {
      raf = 0;
      const items = queue;
      queue = new Set();
      items.forEach((n) => n.isConnected && !window.location.pathname.startsWith('/admin') && walk(n));
    };
    Promise.all([import('./en.json'), import('./en.manual.json')]).then(([auto, manual]) => {
      dict = {};
      Object.entries({ ...(auto.default || auto), ...(manual.default || manual) }).forEach(([k, v]) => { dict[norm(k)] = v; });
      walk(document.body);
      observer = new MutationObserver((muts) => {
        muts.forEach((m) => {
          if (m.type === 'childList') m.addedNodes.forEach((n) => queue.add(n));
          else queue.add(m.target);
        });
        if (!raf) raf = requestAnimationFrame(flush);
      });
      observer.observe(document.body, {
        childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS,
      });
    });
    return () => { observer?.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [active]);

  return null;
};

export default DomTranslator;
